import { Routes, Route, Navigate } from 'react-router-dom';
import { Suspense, lazy } from 'react';
import { PrivateRoute } from '../components/PrivateRoute';
import PublicRoute from '../routes/PublicRoute';
import Layout from '../components/Layout';
import PageLoader from '../components/PageLoader';
import SafeRoute from '../components/common/SafeRoute';
import RequireOrganization from '../components/guards/RequireOrganization';

// Eager load critical components
import Login from '../pages/Login';
import ForgotPassword from '../pages/ForgotPassword';
import ResetPassword from '../pages/ResetPassword';
import LockScreen from '../pages/LockScreen';

// Lazy load route components for code splitting
const Dashboard = lazy(() => import('../pages/Dashboard'));
const Products = lazy(() => import('../pages/Inventory/Products'));
const ProductDetail = lazy(() => import('../pages/Inventory/ProductDetail'));
const ProductAnalytics = lazy(() => import('../pages/Inventory/ProductAnalytics'));
const Movements = lazy(() => import('../pages/Inventory/Movements'));
const SmartReorder = lazy(() => import('../pages/inventory/SmartReorder'));
const HealthMonitor = lazy(() => import('../pages/inventory/HealthMonitor'));
const ABCAnalysis = lazy(() => import('../pages/inventory/ABCAnalysis'));
const SupplierPerformance = lazy(() => import('../pages/procurement/SupplierPerformance'));
const AutomationHub = lazy(() => import('../pages/orders/AutomationHub'));
const CampaignHub = lazy(() => import('../pages/marketing/CampaignHub'));
const BarcodeScanner = lazy(() => import('../pages/barcode/BarcodeScanner'));
const Warehouses = lazy(() => import('../pages/Warehouses'));
const WarehouseDetail = lazy(() => import('../pages/Warehouses/WarehouseDetail'));
const Transfers = lazy(() => import('../pages/Transfers/Transfers'));
const TransferDetail = lazy(() => import('../pages/Transfers/TransferDetail'));
const PurchaseOrders = lazy(() => import('../pages/PurchaseOrders'));
const PODetail = lazy(() => import('../pages/PurchaseOrders/PODetail'));
const CreatePurchaseOrder = lazy(() => import('../pages/CreatePurchaseOrder'));
const Orders = lazy(() => import('../pages/Orders'));
const OrderDetail = lazy(() => import('../pages/Orders/OrderDetail'));
const CreateOrder = lazy(() => import('../pages/CreateOrder'));
const Suppliers = lazy(() => import('../pages/Suppliers'));
const SupplierDetail = lazy(() => import('../pages/Suppliers/SupplierDetail'));
const SupplierScorecard = lazy(() => import('../pages/Suppliers/SupplierScorecard'));
const Analytics = lazy(() => import('../pages/Analytics/Analytics'));
const AdvancedAnalytics = lazy(() => import('../pages/Analytics/AdvancedAnalytics'));
const RevenueAnalytics = lazy(() => import('../pages/RevenueAnalytics'));
const AmazonMetrics = lazy(() => import('../pages/AmazonMetrics'));
const OperationalAlerts = lazy(() => import('../pages/OperationalAlerts'));
const CommandCenter = lazy(() => import('../pages/operations/CommandCenter'));
const ExecutiveDashboard = lazy(() => import('../pages/dashboards/ExecutiveDashboard'));
const OperationsDashboard = lazy(() => import('../pages/dashboards/OperationsDashboard'));
const PurchasingDashboard = lazy(() => import('../pages/dashboards/PurchasingDashboard'));
const AmazonManagerDashboard = lazy(() => import('../pages/dashboards/AmazonManagerDashboard'));
const FBAShipments = lazy(() => import('../pages/FBAShipments'));
const CreateFBAShipment = lazy(() => import('../pages/CreateFBAShipment'));
const FBAShipmentDetail = lazy(() => import('../pages/FBAShipmentDetail'));
const QualityInspections = lazy(() => import('../pages/QualityInspections'));
const CreateInspection = lazy(() => import('../pages/CreateInspection'));
const InspectionDetail = lazy(() => import('../pages/InspectionDetail'));
const CashFlow = lazy(() => import('../pages/CashFlow'));
const CreateTransaction = lazy(() => import('../pages/CreateTransaction'));
const CreateProjection = lazy(() => import('../pages/CreateProjection'));
const FeeCalculator = lazy(() => import('../pages/FeeCalculator'));
const QuickReceive = lazy(() => import('../pages/QuickReceive'));
const QuickInspect = lazy(() => import('../pages/QuickInspect'));
const QuickPack = lazy(() => import('../pages/QuickPack'));
const WarehouseMapView = lazy(() => import('../pages/warehouses/WarehouseMap'));
const IntegrationsHub = lazy(() => import('../pages/integrations/IntegrationsHub'));
const AmazonIntegration = lazy(() => import('../pages/integrations/AmazonIntegration'));
const AmazonCallback = lazy(() => import('../pages/integrations/AmazonCallback'));
const DemandForecasting = lazy(() => import('../pages/forecasting/DemandForecasting'));
const AutoReorder = lazy(() => import('../pages/automation/AutoReorder'));
const Reports = lazy(() => import('../pages/reports/Reports'));
const ReportBuilder = lazy(() => import('../pages/reports/ReportBuilder'));
const ReportBuilderEnhanced = lazy(() => import('../pages/reports/ReportBuilderEnhanced'));
const ReportTemplateLibrary = lazy(() => import('../pages/reports/ReportTemplateLibrary'));
const ScheduledReports = lazy(() => import('../pages/reports/ScheduledReports'));
const AuditLogs = lazy(() => import('../pages/compliance/AuditLogs'));
const CacheMonitor = lazy(() => import('../pages/admin/CacheMonitor'));
const Notifications = lazy(() => import('../pages/notifications/Notifications'));
const AlertManagement = lazy(() => import('../pages/alerts/AlertManagement'));
const DashboardBuilder = lazy(() => import('../pages/analytics/DashboardBuilder'));
const OrganizationSettings = lazy(() => import('../pages/settings/OrganizationSettings'));
const ProfileSettings = lazy(() => import('../pages/settings/ProfileSettings'));
const Settings = lazy(() => import('../pages/Settings'));
const Subscription = lazy(() => import('../pages/SubscriptionV3'));
const SubscriptionV2 = lazy(() => import('../pages/SubscriptionV2'));
const SubscriptionSuccess = lazy(() => import('../pages/SubscriptionSuccess'));
const SubscriptionCancel = lazy(() => import('../pages/SubscriptionCancel'));
const InvoiceHistory = lazy(() => import('../components/InvoiceHistory'));
const PaymentMethods = lazy(() => import('../components/PaymentMethods'));
const UsageAlerts = lazy(() => import('../components/UsageAlerts'));
const SeatManagement = lazy(() => import('../components/SeatManagement'));
const SubscriptionDashboard = lazy(() => import('../components/SubscriptionDashboard'));
const SearchAnalyticsDashboard = lazy(() => import('../pages/SearchAnalyticsDashboard'));
const Pricing = lazy(() => import('../pages/Pricing'));
const GettingStarted = lazy(() => import('../pages/GettingStarted'));
const FeaturesShowcase = lazy(() => import('../pages/FeaturesShowcase'));
const Help = lazy(() => import('../pages/Help'));
const Docs = lazy(() => import('../pages/Docs'));
const NotFound = lazy(() => import('../pages/NotFound'));
const WorkspaceOnboarding = lazy(() => import('../pages/onboarding/WorkspaceOnboarding'));
const PrivacyPolicy = lazy(() => import('../pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('../pages/TermsOfService'));

const AppRouter = () => {
  return (
    <Routes>
      <Route path="/login" element={
        <PublicRoute>
          <Login />
        </PublicRoute>
      } />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/lock" element={<LockScreen />} />

      {/* Public legal pages */}
      <Route path="/privacy" element={
        <Suspense fallback={<PageLoader />}>
          <PrivacyPolicy />
        </Suspense>
      } />
      <Route path="/terms" element={
        <Suspense fallback={<PageLoader />}>
          <TermsOfService />
        </Suspense>
      } />

      {/* Amazon OAuth callback — public route, no auth required.
          Amazon redirects here after Seller Central consent. The AmazonCallback
          component handles code exchange and requires the user's ERP session
          to be active to complete the API call. */}
      <Route path="/oauth/callback/amazon" element={
        <Suspense fallback={<PageLoader />}>
          <AmazonCallback />
        </Suspense>
      } />

      {/* Workspace Onboarding - Outside main layout, requires auth but no org */}
      <Route path="/onboarding/workspace" element={
        <PrivateRoute>
          <Suspense fallback={<PageLoader />}>
            <WorkspaceOnboarding />
          </Suspense>
        </PrivateRoute>
      } />

      <Route path="/" element={
        <PrivateRoute>
          <RequireOrganization>
            <Layout />
          </RequireOrganization>
        </PrivateRoute>
      }>
        <Route index element={<Navigate to="/dashboard" replace />} />

        {/* Wrap all lazy-loaded routes in Suspense */}
        <Route path="dashboard" element={
          <SafeRoute routeName="Dashboard">
            <Dashboard />
          </SafeRoute>
        } />

        {/* Getting Started / Onboarding */}
        <Route path="getting-started" element={
          <SafeRoute routeName="Getting Started">
            <GettingStarted />
          </SafeRoute>
        } />

        {/* Features Showcase */}
        <Route path="features" element={
          <SafeRoute routeName="Features">
            <FeaturesShowcase />
          </SafeRoute>
        } />

        {/* Help & Support */}
        <Route path="help" element={
          <SafeRoute routeName="Help">
            <Help />
          </SafeRoute>
        } />
        <Route path="help/contact" element={
          <SafeRoute routeName="Help">
            <Help />
          </SafeRoute>
        } />

        {/* Documentation */}
        <Route path="docs" element={
          <SafeRoute routeName="Docs">
            <Docs />
          </SafeRoute>
        } />

        {/* Inventory */}
        <Route path="inventory" element={<Navigate to="/inventory/products" replace />} />
        <Route path="inventory/products" element={
          <SafeRoute routeName="Products">
            <Products />
          </SafeRoute>
        } />
        <Route path="inventory/products/new" element={
          <SafeRoute routeName="Products">
            <Products />
          </SafeRoute>
        } />
        <Route path="inventory/products/:id" element={
          <Suspense fallback={<PageLoader />}>
            <ProductDetail />
          </Suspense>
        } />
        <Route path="inventory/products/:id/analytics" element={
          <Suspense fallback={<PageLoader />}>
            <ProductAnalytics />
          </Suspense>
        } />
        <Route path="inventory/movements" element={
          <Suspense fallback={<PageLoader />}>
            <Movements />
          </Suspense>
        } />
        <Route path="inventory/smart-reorder" element={
          <Suspense fallback={<PageLoader />}>
            <SmartReorder />
          </Suspense>
        } />
        <Route path="inventory/health-monitor" element={
          <Suspense fallback={<PageLoader />}>
            <HealthMonitor />
          </Suspense>
        } />
        <Route path="inventory/abc-analysis" element={
          <Suspense fallback={<PageLoader />}>
            <ABCAnalysis />
          </Suspense>
        } />
        <Route path="procurement/supplier-performance" element={
          <Suspense fallback={<PageLoader />}>
            <SupplierPerformance />
          </Suspense>
        } />
        <Route path="orders/automation" element={
          <Suspense fallback={<PageLoader />}>
            <AutomationHub />
          </Suspense>
        } />
        <Route path="marketing/campaigns" element={
          <Suspense fallback={<PageLoader />}>
            <CampaignHub />
          </Suspense>
        } />
        <Route path="barcode" element={
          <Suspense fallback={<PageLoader />}>
            <BarcodeScanner />
          </Suspense>
        } />

        {/* Warehouses */}
        <Route path="warehouses" element={
          <Suspense fallback={<PageLoader />}>
            <Warehouses />
          </Suspense>
        } />
        <Route path="warehouses/map" element={
          <Suspense fallback={<PageLoader />}>
            <WarehouseMapView />
          </Suspense>
        } />
        <Route path="warehouses/:id" element={
          <Suspense fallback={<PageLoader />}>
            <WarehouseDetail />
          </Suspense>
        } />

        {/* Transfers */}
        <Route path="transfers" element={
          <Suspense fallback={<PageLoader />}>
            <Transfers />
          </Suspense>
        } />
        <Route path="warehouse-transfers" element={
          <Suspense fallback={<PageLoader />}>
            <Transfers />
          </Suspense>
        } />
        <Route path="transfers/:id" element={
          <Suspense fallback={<PageLoader />}>
            <TransferDetail />
          </Suspense>
        } />

        {/* Purchase Orders */}
        <Route path="purchase-orders" element={
          <Suspense fallback={<PageLoader />}>
            <PurchaseOrders />
          </Suspense>
        } />
        <Route path="purchase-orders/:id" element={
          <Suspense fallback={<PageLoader />}>
            <PODetail />
          </Suspense>
        } />
        <Route path="purchase-orders/create" element={
          <Suspense fallback={<PageLoader />}>
            <CreatePurchaseOrder />
          </Suspense>
        } />
        <Route path="purchase-orders/new" element={
          <Suspense fallback={<PageLoader />}>
            <CreatePurchaseOrder />
          </Suspense>
        } />

        {/* Orders */}
        <Route path="orders" element={
          <SafeRoute routeName="Orders">
            <Orders />
          </SafeRoute>
        } />
        <Route path="orders/:id" element={
          <Suspense fallback={<PageLoader />}>
            <OrderDetail />
          </Suspense>
        } />
        <Route path="orders/new" element={
          <Suspense fallback={<PageLoader />}>
            <SafeRoute routeName="CreateOrder">
              <CreateOrder />
            </SafeRoute>
          </Suspense>
        } />

        {/* Suppliers */}
        <Route path="suppliers" element={
          <Suspense fallback={<PageLoader />}>
            <Suppliers />
          </Suspense>
        } />
        <Route path="suppliers/:id" element={
          <Suspense fallback={<PageLoader />}>
            <SupplierDetail />
          </Suspense>
        } />
        <Route path="suppliers/:id/scorecard" element={
          <Suspense fallback={<PageLoader />}>
            <SupplierScorecard />
          </Suspense>
        } />

        {/* Analytics */}
        <Route path="analytics" element={
          <Suspense fallback={<PageLoader />}>
            <Analytics />
          </Suspense>
        } />
        <Route path="analytics/advanced" element={
          <Suspense fallback={<PageLoader />}>
            <AdvancedAnalytics />
          </Suspense>
        } />
        <Route path="analytics/revenue" element={
          <Suspense fallback={<PageLoader />}>
            <RevenueAnalytics />
          </Suspense>
        } />
        <Route path="analytics/amazon" element={
          <Suspense fallback={<PageLoader />}>
            <AmazonMetrics />
          </Suspense>
        } />
        <Route path="operations/command" element={
          <Suspense fallback={<PageLoader />}>
            <SafeRoute routeName="Operations Command Center">
              <CommandCenter />
            </SafeRoute>
          </Suspense>
        } />
        <Route path="analytics/alerts" element={
          <Suspense fallback={<PageLoader />}>
            <OperationalAlerts />
          </Suspense>
        } />
        <Route path="analytics/search" element={
          <Suspense fallback={<PageLoader />}>
            <SearchAnalyticsDashboard />
          </Suspense>
        } />

        {/* Role-Based Dashboards */}
        <Route path="dashboards/executive" element={
          <Suspense fallback={<PageLoader />}>
            <ExecutiveDashboard />
          </Suspense>
        } />
        <Route path="dashboards/operations" element={
          <Suspense fallback={<PageLoader />}>
            <OperationsDashboard />
          </Suspense>
        } />
        <Route path="dashboards/purchasing" element={
          <Suspense fallback={<PageLoader />}>
            <PurchasingDashboard />
          </Suspense>
        } />
        <Route path="dashboards/amazon" element={
          <Suspense fallback={<PageLoader />}>
            <AmazonManagerDashboard />
          </Suspense>
        } />

        {/* FBA Shipments */}
        <Route path="fba-shipments" element={
          <Suspense fallback={<PageLoader />}>
            <FBAShipments />
          </Suspense>
        } />
        <Route path="fba-shipments/new" element={
          <Suspense fallback={<PageLoader />}>
            <CreateFBAShipment />
          </Suspense>
        } />
        <Route path="fba-shipments/:id" element={
          <Suspense fallback={<PageLoader />}>
            <FBAShipmentDetail />
          </Suspense>
        } />

        {/* Amazon FBA Shipments (alternative path) */}
        <Route path="amazon/fba-shipments" element={
          <Suspense fallback={<PageLoader />}>
            <FBAShipments />
          </Suspense>
        } />
        <Route path="amazon/fba-shipments/create" element={
          <Suspense fallback={<PageLoader />}>
            <CreateFBAShipment />
          </Suspense>
        } />
        <Route path="amazon/fba-shipments/:id" element={
          <Suspense fallback={<PageLoader />}>
            <FBAShipmentDetail />
          </Suspense>
        } />
        <Route path="amazon/fee-calculator" element={
          <Suspense fallback={<PageLoader />}>
            <FeeCalculator />
          </Suspense>
        } />

        {/* Quality Control */}
        <Route path="quality/inspections" element={
          <Suspense fallback={<PageLoader />}>
            <QualityInspections />
          </Suspense>
        } />
        <Route path="quality/inspections/new" element={
          <SafeRoute routeName="Create Inspection">
            <CreateInspection />
          </SafeRoute>
        } />
        <Route path="quality/inspections/:id" element={
          <Suspense fallback={<PageLoader />}>
            <InspectionDetail />
          </Suspense>
        } />

        {/* Finance & Cash Flow */}
        <Route path="finance" element={
          <Suspense fallback={<PageLoader />}>
            <CashFlow />
          </Suspense>
        } />
        <Route path="cash-flow" element={
          <Suspense fallback={<PageLoader />}>
            <CashFlow />
          </Suspense>
        } />
        <Route path="finance/transactions/new" element={
          <Suspense fallback={<PageLoader />}>
            <CreateTransaction />
          </Suspense>
        } />
        <Route path="finance/projections/new" element={
          <Suspense fallback={<PageLoader />}>
            <CreateProjection />
          </Suspense>
        } />
        <Route path="finance/calculator" element={
          <Suspense fallback={<PageLoader />}>
            <FeeCalculator />
          </Suspense>
        } />

        {/* Barcode Scanner */}
        <Route path="scanner/receive" element={
          <Suspense fallback={<PageLoader />}>
            <QuickReceive />
          </Suspense>
        } />
        <Route path="scanner/inspect" element={
          <Suspense fallback={<PageLoader />}>
            <QuickInspect />
          </Suspense>
        } />
        <Route path="scanner/pack" element={
          <Suspense fallback={<PageLoader />}>
            <QuickPack />
          </Suspense>
        } />

        {/* Integrations */}
        <Route path="integrations" element={
          <SafeRoute routeName="Integrations">
            <IntegrationsHub />
          </SafeRoute>
        } />
        <Route path="integrations/amazon" element={
          <SafeRoute routeName="Amazon Integration">
            <AmazonIntegration />
          </SafeRoute>
        } />
        <Route path="integrations/amazon/callback" element={
          <SafeRoute routeName="Amazon Callback">
            <AmazonCallback />
          </SafeRoute>
        } />

        {/* Forecasting */}
        <Route path="forecasting" element={
          <Suspense fallback={<PageLoader />}>
            <DemandForecasting />
          </Suspense>
        } />

        {/* Automation */}
        <Route path="automation/reorder" element={
          <Suspense fallback={<PageLoader />}>
            <AutoReorder />
          </Suspense>
        } />

        {/* Reports */}
        <Route path="reports" element={
          <Suspense fallback={<PageLoader />}>
            <Reports />
          </Suspense>
        } />
        <Route path="reports/builder" element={
          <Suspense fallback={<PageLoader />}>
            <ReportBuilder />
          </Suspense>
        } />
        <Route path="reports/enhanced-builder" element={
          <Suspense fallback={<PageLoader />}>
            <ReportBuilderEnhanced />
          </Suspense>
        } />
        <Route path="reports/templates" element={
          <Suspense fallback={<PageLoader />}>
            <ReportTemplateLibrary />
          </Suspense>
        } />
        <Route path="reports/scheduled" element={
          <Suspense fallback={<PageLoader />}>
            <ScheduledReports />
          </Suspense>
        } />

        {/* Audit & Compliance */}
        <Route path="audit-logs" element={
          <Suspense fallback={<PageLoader />}>
            <AuditLogs />
          </Suspense>
        } />

        {/* Admin Tools */}
        <Route path="admin/cache-monitor" element={
          <Suspense fallback={<PageLoader />}>
            <CacheMonitor />
          </Suspense>
        } />

        {/* Dashboard Builder */}
        <Route path="analytics/dashboards" element={
          <Suspense fallback={<PageLoader />}>
            <DashboardBuilder />
          </Suspense>
        } />

        {/* Notifications & Alerts */}
        <Route path="notifications" element={
          <Suspense fallback={<PageLoader />}>
            <Notifications />
          </Suspense>
        } />
        <Route path="alerts" element={
          <Suspense fallback={<PageLoader />}>
            <AlertManagement />
          </Suspense>
        } />

        {/* Settings */}
        <Route path="settings" element={
          <Suspense fallback={<PageLoader />}>
            <Settings />
          </Suspense>
        } />
        <Route path="settings/profile" element={
          <Suspense fallback={<PageLoader />}>
            <ProfileSettings />
          </Suspense>
        } />
        <Route path="settings/organization" element={
          <Suspense fallback={<PageLoader />}>
            <OrganizationSettings />
          </Suspense>
        } />
        <Route path="settings/organization/:tab" element={
          <Suspense fallback={<PageLoader />}>
            <OrganizationSettings />
          </Suspense>
        } />

        {/* Subscription & Billing */}
        <Route path="pricing" element={
          <Suspense fallback={<PageLoader />}>
            <Pricing />
          </Suspense>
        } />
        <Route path="subscription" element={
          <Suspense fallback={<PageLoader />}>
            <Subscription />
          </Suspense>
        } />
        <Route path="subscription-v2" element={
          <Suspense fallback={<PageLoader />}>
            <SubscriptionV2 />
          </Suspense>
        } />
        <Route path="subscription/success" element={
          <Suspense fallback={<PageLoader />}>
            <SubscriptionSuccess />
          </Suspense>
        } />
        <Route path="subscription/cancel" element={
          <Suspense fallback={<PageLoader />}>
            <SubscriptionCancel />
          </Suspense>
        } />
        {/* Subscription aliases for UX consistency */}
        <Route path="subscription/manage" element={<Navigate to="/subscription" replace />} />
        <Route path="billing" element={<Navigate to="/subscription" replace />} />
        <Route path="settings/billing" element={<Navigate to="/subscription" replace />} />
        <Route path="settings/subscription" element={<Navigate to="/subscription" replace />} />
        <Route path="settings/integrations" element={<Navigate to="/integrations" replace />} />
        
        <Route path="invoices" element={
          <Suspense fallback={<PageLoader />}>
            <InvoiceHistory />
          </Suspense>
        } />
        <Route path="payment-methods" element={
          <Suspense fallback={<PageLoader />}>
            <PaymentMethods />
          </Suspense>
        } />
        <Route path="usage-alerts" element={
          <Suspense fallback={<PageLoader />}>
            <UsageAlerts />
          </Suspense>
        } />
        <Route path="seat-management" element={
          <Suspense fallback={<PageLoader />}>
            <SeatManagement />
          </Suspense>
        } />
        <Route path="subscription-analytics" element={
          <Suspense fallback={<PageLoader />}>
            <SubscriptionDashboard />
          </Suspense>
        } />

        {/* 404 Catch-all inside authenticated layout */}
        <Route path="*" element={
          <Suspense fallback={<PageLoader />}>
            <NotFound />
          </Suspense>
        } />
      </Route>

      {/* 404 Catch-all for unauthenticated routes */}
      <Route path="*" element={
        <Suspense fallback={<PageLoader />}>
          <NotFound />
        </Suspense>
      } />
    </Routes>
  );
};

export default AppRouter;
